<template>
  <div
    class="flex transition-all duration-200 ease-in-out-circ"
    :class="{
      'pl-0': isHidden || (isFloating && isMobileNav),
      'pl-20': isCollapsed || (isFloating && !isMobileNav),
      'pl-56': isExpanded,
      'bg-gray-300': useBackgroundColor
    }"
  >
    <NavList
      :user-initials="userInitials"
      :user-first-name="firstName"
      :user-last-name="lastName"
    />

    <div class="flex-1 flex flex-col h-dvh overflow-x-hidden">
      <TopBar @mobile-open="onMobileOpen" />

      <div class="mx-4 sm:mx-12 mt-6">
        <slot name="pageAlerts" />
      </div>

      <div
        v-if="$slots.pageTitle"
        class="flex flex-col-reverse md:flex-row justify-between items-start md:items-center my-5 mx-4 sm:mx-12"
      >
        <div class="flex gap-x-6">
          <h2 class="text-2xl font-semibold text-blue-600">
            <slot name="pageTitle" />
          </h2>
          <div>
            <slot name="pageTitleWidgets" />
          </div>
        </div>
        <div class="flex mb-2 md:mt-0 md:ml-4 md:items-start items-end self-end">
          <slot name="headingOptions"></slot>
        </div>
      </div>

      <div
        class="flex-1 flex flex-col"
        :class="{
          'overflow-auto': overflow,
          'overflow-hidden': pageLayout.shouldLock.value
        }"
      >
        <slot></slot>
      </div>
    </div>
  </div>

  <slot name="overlays" />
  <slot name="modals" />

  <CustomerCheckInModal
    v-model:open="showingCheckInModal"
    :customer="customerToCheckIn"
    @complete="checkInComplete"
    :location-id="currentLocationId"
  />

  <Confirm />

  <!-- Global notification live region, render this permanently at the end of the document -->
  <div
    aria-live="assertive"
    class="fixed z-50 inset-0 flex items-end px-4 py-6 pointer-events-none sm:p-6 sm:items-start"
  >
    <div class="w-full flex flex-col items-center space-y-4 sm:items-end">
      <!-- Notification panel, dynamically insert this into the live region when it needs to be displayed -->
      <transition
        enter-active-class="ease-out duration-300 transition"
        enter-from-class="translate-y-2 opacity-0 sm:translate-y-0 sm:translate-x-2"
        enter-to-class="translate-y-0 opacity-100 sm:translate-x-0"
        leave-active-class="transition ease-in duration-100"
        leave-from-class="opacity-100"
        leave-to-class="opacity-0"
      >
        <div
          v-if="showingGlobalSuccess"
          class="max-w-sm w-full bg-gray-100 shadow-lg rounded-lg pointer-events-auto ring-1 ring-gray-400 ring-opacity-5 overflow-hidden"
        >
          <div class="p-4">
            <div class="flex items-start">
              <div class="ml-3 w-0 flex-1 pt-0.5">
                <p class="text-sm font-medium text-gray-900">Success!</p>
                <p class="mt-1 text-sm text-gray-600">
                  {{ successGlobal }}
                </p>
              </div>
              <div class="ml-4 shrink-0 flex">
                <button
                  @click="showingGlobalSuccess = false"
                  class="bg-gray-100 rounded-md inline-flex text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
                >
                  <span class="sr-only">Close</span>
                  <XIcon
                    class="h-5 w-5"
                    aria-hidden="true"
                  />
                </button>
              </div>
            </div>
          </div>
        </div>
      </transition>
    </div>
  </div>
  <slot name="scripts" />
</template>

<script setup>
import { provide } from 'vue'
import { useUrlSearchParams } from '@vueuse/core'
import { useNavigation } from '@/Components/Navigation/navigation'
import NavList from '@/Components/Navigation/NavList.vue'
import TopBar from '@/Components/TopBar.vue'
import Confirm from '@/Components/Modals/Confirm.vue'
import { PAGE_URL_PARAMS } from '@/utils/symbols'
import { useLayoutState } from '@/Layouts/layout-state'

const urlParams = useUrlSearchParams('history')
provide(PAGE_URL_PARAMS, urlParams)

const pageLayout = useLayoutState()

const props = defineProps({
  overflow: {
    type: Boolean,
    default: false
  },
  useBackgroundColor: {
    type: Boolean,
    default: true
  }
})

const { isExpanded, isHidden, isFloating, isCollapsed, isMobileNav, onMobileOpen } = useNavigation()
</script>

<script>
// @Note: This is ported more or less as-is from the old layout.
import { useForm } from '@inertiajs/vue3'
import { XIcon } from '@heroicons/vue/solid'
import hasAuth from '@/Mixins/hasAuth'
import CustomerCheckInModal from '@/Components/Modals/Customers/CheckInModal'
import { datadogRum } from '@datadog/browser-rum'

export default {
  mixins: [hasAuth],
  components: {
    XIcon,
    CustomerCheckInModal
  },
  data() {
    return {
      showingGlobalSuccess: false,
      currentLocationForm: {
        location_id: null
      },
      showingCheckInModal: false,
      customerToCheckIn: null
    }
  },
  created() {
    this.currentLocationForm = useForm({
      location_id: this.currentLocationId
    })

    this.showingGlobalSuccess = !!this.successGlobal
  },
  mounted() {
    if (process.env.MIX_DATADOG_RUM_SESSION_REPLAY_ENABLED) {
      datadogRum.startSessionReplayRecording()
    }
  },

  computed: {
    successGlobal() {
      return this.$page.props.flash.success_global
    }
  },

  watch: {
    successGlobal: function (newVal) {
      this.showingGlobalSuccess = !!newVal
    }
  },

  methods: {
    setCurrentLocation() {
      this.currentLocationForm.post(this.route('locations.current.store'), {
        onSuccess: () => {}
      })
    },
    scrollIntoView() {
      this.$refs.results.children[this.highlightedIndex].scrollIntoView({ block: 'nearest' })
    },

    checkInCustomer(item) {
      axios.get(this.route('customers.show', { customer: item.id, json: true })).then((response) => {
        this.customerToCheckIn = response.data
        this.showingCheckInModal = true
        this.clearSearch()
      })
    },
    checkInComplete() {
      this.customerToCheckIn = null
      this.showingCheckInModal = false
      this.clearSearch()
    }
  }
}
</script>
